import { useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PropertyFilters from "@/components/PropertyFilters";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useLocations } from "@/hooks/useLocations";
import { useProperties } from "@/hooks/useProperties";
import { applyPropertySearchFilters, sanitizePropertySearchFilters, type PropertySearchFilters } from "@/lib/propertyFilters";
import FloatingContactIcons from "@/components/FloatingContactIcons";

const defaultSaleFilters: PropertySearchFilters = {
  listingType: "sale",
  category: "ready",
  locationId: "",
  bedrooms: "",
  maxPrice: "",
};

const PropertiesForSalePage = () => {
  const [filters, setFilters] = useState<PropertySearchFilters>(defaultSaleFilters);
  const { data: saleProperties, isLoading } = useProperties({ listing_type: "sale" });
  const { data: locations = [], isLoading: loadingLocations } = useLocations();
  const offplanCount = useMemo(() => saleProperties?.filter((property) => property.category === "offplan").length ?? 0, [saleProperties]);
  const underConstructionCount = useMemo(() => saleProperties?.filter((property) => property.category === "under-construction").length ?? 0, [saleProperties]);
  const readyCount = useMemo(() => saleProperties?.filter((property) => property.category === "ready").length ?? 0, [saleProperties]);
  const filteredProperties = useMemo(() => applyPropertySearchFilters(saleProperties ?? [], filters), [saleProperties, filters]);

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        <div className="px-4 md:px-16 py-6">
          <nav className="flex items-center gap-2 text-sm text-primary-foreground/70 flex-wrap">
            <a href="/" className="hover:text-primary-foreground transition-colors">Home</a>
            <span>/</span><span>Properties for Sale</span>
          </nav>
        </div>

        <div className="px-4 md:px-16 py-8 md:py-12">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="font-display text-3xl md:text-5xl font-light text-logo-gold-gradient mb-4 md:mb-6">All Properties for Sale</h1>
            <p className="font-body text-base md:text-lg text-primary-foreground/75 max-w-3xl mx-auto px-4">
              Browse our complete collection of premium properties for sale in Nairobi's most sought-after neighborhoods
            </p>
          </div>

          <div className="flex justify-center mb-8 md:mb-12">
            <div className="grid w-full max-w-2xl grid-cols-1 rounded-lg border border-primary-foreground/15 bg-background/10 p-1 backdrop-blur-sm sm:grid-cols-3">
              <button onClick={() => setFilters((current) => sanitizePropertySearchFilters({ ...current, category: "offplan" }, "sale"))} className={`px-4 md:px-6 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase leading-tight transition-all duration-300 rounded-md ${filters.category === "offplan" ? "bg-logo-gold-gradient shadow-logo-gold border border-logo-gold text-[#3A2A0D]" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>
                Offplan ({offplanCount})
              </button>
              <button onClick={() => setFilters((current) => sanitizePropertySearchFilters({ ...current, category: "under-construction" }, "sale"))} className={`px-4 md:px-6 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase leading-tight transition-all duration-300 rounded-md ${filters.category === "under-construction" ? "bg-logo-gold-gradient shadow-logo-gold border border-logo-gold text-[#3A2A0D]" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>
                Under Construction ({underConstructionCount})
              </button>
              <button onClick={() => setFilters((current) => sanitizePropertySearchFilters({ ...current, category: "ready" }, "sale"))} className={`px-4 md:px-6 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase leading-tight transition-all duration-300 rounded-md ${filters.category === "ready" ? "bg-logo-gold-gradient shadow-logo-gold border border-logo-gold text-[#3A2A0D]" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>
                Ready ({readyCount})
              </button>
            </div>
          </div>

          <div className="max-w-7xl mx-auto">
            <PropertyFilters
              value={filters}
              locations={locations}
              loadingLocations={loadingLocations}
              onChange={setFilters}
              onReset={() => setFilters(defaultSaleFilters)}
              fixedListingType="sale"
            />
          </div>
        </div>

        <div className="px-4 md:px-16 pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="font-body text-sm text-primary-foreground/75 mb-6">
              Showing <span className="text-primary-foreground font-semibold">{filteredProperties.length}</span> properties for sale.
            </p>

            {isLoading ? (
              <p className="text-center text-primary-foreground/70 font-body py-12">Loading properties...</p>
            ) : filteredProperties.length > 0 ? (
              <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredProperties.map((property) => (
                  <PropertyListingCard key={property.id} {...property} statusLabel={property.status_label} categoryLabel={property.category_label} />
                ))}
              </div>
            ) : (
              <p className="text-center text-primary-foreground/70 font-body py-12">No sale properties match the selected filters.</p>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <FloatingContactIcons />
    </div>
  );
};

export default PropertiesForSalePage;
